import React from 'react';
import {
  Button,
  Input,
  Message,
  Table,
  Tooltip,
  Whisper,
  useToaster,
} from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import DeleteIcon from '@rsuite/icons/Trash';
import * as users from '../../api/users';
import EditUserModal from './EditUserModal';
import CreateUserModal from './CreateUserModal';
import PageLayout from '../../common/PageLayout';
import ColumnsButton from '../../common/ColumnsButton';
import { useTableColumns } from '../../common/useTableColumns';
import { useResourceList } from '../../common/useResourceList';
import { formatDate, getRowClassName } from '../../common/formatters';
import { renderRowNumberColumn } from '../../common/RowNumberColumn';
import { renderTableEmpty, renderTableLoading } from '../../common/TableStates';
import TablePagination from '../../common/TablePagination';
import { useFormErrors } from '../../common/useFormErrors';
import { useConfirm } from '../../common/useConfirm';

const { Column, HeaderCell, Cell } = Table;

const TABLE_COLUMNS = [
  { value: 'email', label: 'Пошта', display: true, flexGrow: 3, minWidth: 200 },
  {
    value: 'createdAt',
    label: 'Створено',
    display: true,
    flexGrow: 1,
    minWidth: 90,
  },
  {
    value: 'updatedAt',
    label: 'Оновлено',
    display: true,
    flexGrow: 1,
    minWidth: 90,
  },
];

type User = {
  id: string;
  email: string;
  createdAt: number | null;
  updatedAt: number | null;
};

export default function Users() {
  const toaster = useToaster();
  const [confirm, ConfirmDialog] = useConfirm();
  const [email, setEmail] = React.useState('');

  const [tableColumns, , toggleColumn] = useTableColumns(
    TABLE_COLUMNS,
    'users:tableColumns:v2'
  );

  const [modalUserId, setModalUserId] = React.useState<string>('');
  const [createModalOpen, setCreateModalOpen] = React.useState<boolean>(false);
  const handleModalClose = () => {
    setModalUserId('');
    setModalUserEmail('');
    setModalUserPassword('');
  };
  const [modalUserEmail, setModalUserEmail] = React.useState<string>('');
  const [modalUserPassword, setModalUserPassword] = React.useState<string>('');
  const {
    errorSummary: updateErrors,
    setApiError: setUpdateError,
    clearErrors: clearUpdateErrors,
  } = useFormErrors({
    fallbackMessage: 'Не вдалося оновити користувача. Спробуйте пізніше',
    resetOn: [modalUserId],
  });

  const list = useResourceList<User>({
    fetch: ({ page, perPage }) => users.list(page, perPage, email || undefined),
    filters: [email],
  });

  const updateUser = async () => {
    if (!modalUserId) {
      return false;
    }

    clearUpdateErrors();
    try {
      await users.update(
        modalUserId,
        modalUserEmail.length ? modalUserEmail : undefined,
        modalUserPassword.length ? modalUserPassword : undefined
      );
      return true;
    } catch (err: any) {
      console.error(err);
      const errorMessage = setUpdateError(err)[0];
      toaster.push(
        <Message type="error" showIcon closable>
          {errorMessage}
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
      return false;
    }
  };

  const firstVisibleColumnValue = React.useMemo(
    () => tableColumns.find(column => column.display !== false)?.value,
    [tableColumns]
  );

  const renderColumn = (column: (typeof TABLE_COLUMNS)[0]) => {
    if (column.display === false) {
      return null;
    }

    const commonProps = {
      key: column.value,
      flexGrow: column.flexGrow ?? 1,
      minWidth: column.minWidth || 80,
      fixed:
        column.value === firstVisibleColumnValue
          ? ('left' as const)
          : undefined,
    };

    switch (column.value) {
      case 'email':
        return (
          <Column {...commonProps} fullText={true}>
            <HeaderCell>Пошта</HeaderCell>
            <Cell dataKey="email" />
          </Column>
        );
      case 'createdAt':
        return (
          <Column {...commonProps}>
            <HeaderCell>Створено</HeaderCell>
            <Cell
              dataKey="createdAt"
              renderCell={(content: number) => formatDate(String(content))}
            />
          </Column>
        );
      case 'updatedAt':
        return (
          <Column {...commonProps}>
            <HeaderCell>Оновлено</HeaderCell>
            <Cell
              dataKey="updatedAt"
              renderCell={(content: number) => formatDate(String(content))}
            />
          </Column>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {ConfirmDialog}
      <PageLayout
        title="Адміністратори"
        actions={
          <>
            <Input
              className="toolbar-search"
              placeholder="Пошук за поштою"
              value={email || ''}
              onChange={setEmail}
            />
            <ColumnsButton columns={tableColumns} onToggle={toggleColumn} />
            <Button
              appearance="primary"
              onClick={() => setCreateModalOpen(true)}
            >
              Створити
            </Button>
          </>
        }
      >
        <div className="section-card">
          <Table
            loading={list.loading}
            autoHeight={true}
            affixHeader={true}
            affixHorizontalScrollbar={true}
            data={list.data}
            renderLoading={renderTableLoading}
            renderEmpty={renderTableEmpty}
            rowClassName={getRowClassName}
          >
            {renderRowNumberColumn({ page: list.page, perPage: list.perPage })}

            {tableColumns.map(renderColumn)}

            <Column fixed="right" width={100}>
              <HeaderCell>Дії</HeaderCell>

              <Cell style={{ padding: '4px 6px' }}>
                {rowData => (
                  <div className="action-icons">
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Редагувати</Tooltip>}
                    >
                      <Button
                        appearance="link"
                        onClick={() => {
                          setModalUserId(rowData.id);
                          setModalUserEmail(rowData.email);
                        }}
                      >
                        <EditIcon />
                      </Button>
                    </Whisper>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Видалити</Tooltip>}
                    >
                      <Button
                        appearance="subtle"
                        className="delete-btn"
                        onClick={async () => {
                          const result = await confirm({
                            message: `Ви впевнені, що хочете видалити користувача ${rowData.email}?`,
                            destructive: true,
                            confirmText: 'Видалити',
                          });
                          if (!result) {
                            return;
                          }
                          users
                            .remove(rowData.id)
                            .catch(console.error)
                            .then(() => list.reload());
                        }}
                      >
                        <DeleteIcon />
                      </Button>
                    </Whisper>
                  </div>
                )}
              </Cell>
            </Column>
          </Table>
          <TablePagination
            total={list.totalAmount}
            activePage={list.page}
            limit={list.perPage}
            limitOptions={[15, 30, 50, 100]}
            onChangeLimit={list.setPerPage}
            onChangePage={list.goToPage}
          />
        </div>

        <EditUserModal
          modalUserId={modalUserId}
          modalUserEmail={modalUserEmail}
          modalUserPassword={modalUserPassword}
          handleModalClose={handleModalClose}
          setModalUserEmail={setModalUserEmail}
          setModalUserPassword={setModalUserPassword}
          submit={async () => {
            const didUpdate = await updateUser();
            if (!didUpdate) {
              return;
            }
            handleModalClose();
            list.reload();
          }}
          errorSummary={updateErrors}
        />

        <CreateUserModal
          open={createModalOpen}
          setOpen={setCreateModalOpen}
          callback={() => {
            list.reload();
          }}
        />
      </PageLayout>
    </>
  );
}
